import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import ClientInfoForm from './ClientInfoForm';
import CardInfoForm from './CardInfoForm';

import NewPaymentCard from '../../../Components/Cards/NewPaymentCard/NewPaymentCard';
import PseInfoForm from './PseInfoForm/PseInfoForm';
import InsuranceAcquisitionCard from '../../../Components/Cards/InsuranceAcquisitionCard';
import {
  PAYMENT_METHOD,
  PAYMENT_VALUE_OPTION,
  TYPE_OF_PAYMENTS,
  getCouponValue,
  getPaymentMethodLabel,
  BREB_MIN_VALUE,
  BREB_MAX_VALUE
} from '../payment_enums';
import { moneyWithDots } from '../../../Utils/Format/MoneyFormat';
import NewCouponCard from '../../../Components/Cards/NewCouponCard';
import PaymentValueOptions from './PaymentValueOptions';
import PaymentMethodOptions from './PaymentMethod';
import PseImage from '../../../Assets/images/pse_logo_v2.png';
import BrebImage from '../../../Assets/images/breb_logo.png';
import {
  CheckPartialPaymentAPI,
  ValidatePartialPaymentAPI
} from '../../../API/Debts/DebtsAPI';
import { ValidPartialPaymentProductTypeIds } from '../../Debts/myDebts_enums';
import ModalProgress from '../../../Components/Progress/Modal/ModalProgress';
import { RecaptchaAction } from '../../../Enums/recaptcha';
import TurnstileInvisibleCaptcha from '../../../Components/Captcha/TurnstileInvisibleCaptcha';
import {
  CurrentStepIndexContext,
  SetCurrentStepIndexContext
} from '../../../Contexts/StepperContext';
import { useForm, FormProvider } from 'react-hook-form';
import { COUPON_VALUES } from '../../../Utils/enums';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';
import SweetAlert from '../../../Components/Alerts/SweetAlert';
import { ALERT_TYPE } from '../../../Components/Alerts/alert_enums';
import useScreenTimeout from '../../../Hooks/useScreenTimeout';
import { history } from '../../../Routes/history';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import {
  pseInfoSchema,
  getFormDefaultValuesPse
} from './PseInfoForm/PseInfoForm';
import {
  brebInfoSchema,
  getFormDefaultValuesBreb
} from './BrebInfoForm/BrebInfoForm';
import { PortalCheck } from '../../../Configs/general';
import { yupResolver } from '@hookform/resolvers/yup';
import { config } from '../../../Configs';
import { useCaptchaWithFallback } from '../../../Hooks/useCaptchaWithFallback';
import BrebInfoForm from './BrebInfoForm/BrebInfoForm';
import BrebAmountDialog from '../../../Components/Dialogs/BrebAmountDialog';

const INSURANCE_VALID_VALUE_OPTIONS = [
  PAYMENT_VALUE_OPTION.TotalCoupon,
  PAYMENT_VALUE_OPTION.CurrentDebt
];

const SEVEN_MINUTES_SECONDS = 7 * 60;
const TWO_MINUTES_SECONDS = 2 * 60;

const TOTAL_TIMEOUT =
  parseInt(config.payments.timeout, 10) || SEVEN_MINUTES_SECONDS;

const NOTIFICATION_DELAY =
  parseInt(config.payments.notificationDelay, 10) || TWO_MINUTES_SECONDS;

const PaymentsSecondStep = props => {
  const {
    formId,
    setGoBack,
    handleGoBack,
    setCurrentStep: setVisualStepperIndex,
    setNextButtonText,
    handleGoToCardStep,
    handlePaymentByType,
    payment,
    methodSelected,
    isCardStep,
    setPayerInfo,
    setInsuranceInfo,
    setDisabledNext,
    setShowPlanInfo,
    showPlanInfo,
    silverPlanPrice = null,
    isValidForInsurance = false,
    currentUser,
    setMethodSelected,
    setPaymentValueOption,
    paymentValueOption,
    setPayment,
    isMultiplePayment,
    paymentsCount,
    isFinancing,
    isMeterRecharge,
    isBrebPaymentMethodPending
  } = props;

  const currentStepIndex = useContext(CurrentStepIndexContext);
  const setCurrentStep = useContext(SetCurrentStepIndexContext);
  const setAlert = useContext(AlertsDispatchContext);

  const handleTimeout = useCallback(
    () => history.replace(ROUTE_NAMES.home),
    []
  );

  const {
    showBanner: showTimeoutBanner,
    formattedCountdown,
    clear: clearScreenTimeout
  } = useScreenTimeout({
    totalSeconds: TOTAL_TIMEOUT,
    notificationDelaySeconds: NOTIFICATION_DELAY,
    onTimeout: handleTimeout
  });

  // * STATE HOOKS;
  const [isDisabledPse, setIsDisabledPse] = useState(false);
  const [isDisabledBreb, setIsDisabledBreb] = useState(false);
  const [showBrebAmountAlert, setShowBrebAmountAlert] = useState(false);

  const [isDisabledInsurance, setIsDisabledInsurance] = useState(false);
  const [loading, setLoading] = useState(false);

  // * OTHER HOOKS
  const classes = useStyles();

  // Validar que BREB solo esté disponible para GDC
  const isBrebAvailable = PortalCheck.isGascaribe;

  const isBrebMethod = methodSelected === PAYMENT_METHOD.BREB;

  // El resolver se lee desde este ref para poder validar contra el
  // schema de PSE o de BREB según el método seleccionado, sin depender
  // de si react-hook-form recalcula el resolver en cada render.
  const activeSchemaRef = useRef(pseInfoSchema);
  activeSchemaRef.current = isBrebMethod ? brebInfoSchema : pseInfoSchema;

  const resolver = useCallback(
    (values, context, options) =>
      yupResolver(activeSchemaRef.current)(values, context, options),
    []
  );

  const methods = useForm({
    resolver,
    context: {
      // Necesario para no validar el campo de nombre de usuario en Efigas
      // cuando el usuario no está autenticado
      isEfigas: PortalCheck.isEfigas,
      isLoggedIn: Boolean(currentUser)
    },
    mode: 'onChange',
    defaultValues: getFormDefaultValuesPse(currentUser)
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(
      isBrebMethod
        ? getFormDefaultValuesBreb(currentUser)
        : getFormDefaultValuesPse(currentUser)
    );
  }, [isBrebMethod, currentUser, methodSelected, reset]);

  const {
    captchaRef: validateCaptchaRef,
    handlers: validateCaptchaHandlers,
    getTokenWithFallback: getValidateToken
  } = useCaptchaWithFallback();
  const {
    captchaRef: checkCaptchaRef,
    handlers: checkCaptchaHandlers,
    getTokenWithFallback: getCheckToken
  } = useCaptchaWithFallback();

  // * FUNCTIONS

  const isBrebAmountValid = useCallback(() => {
    if (!isBrebAvailable) {
      return false;
    }

    const value = getCouponValue(payment);

    return value >= BREB_MIN_VALUE && value <= BREB_MAX_VALUE;
  }, [isBrebAvailable, payment]);

  const validateCouponValue = useCallback(() => {
    const value = getCouponValue(payment);

    const canPay = value >= COUPON_VALUES.MIN_VALUE;
    setDisabledNext(!canPay);

    return canPay;
  }, [payment, setDisabledNext]);

  const onBackward = useCallback(() => {
    handleGoBack();
    clearScreenTimeout();
    setCurrentStep(currentStep => currentStep - 1);
  }, [setCurrentStep, handleGoBack, clearScreenTimeout]);

  const onForward = useCallback(() => {
    clearScreenTimeout();

    if (methodSelected === PAYMENT_METHOD.CARD) {
      handleGoToCardStep();
    } else {
      handlePaymentByType();
    }
  }, [
    handlePaymentByType,
    handleGoToCardStep,
    methodSelected,
    clearScreenTimeout
  ]);

  useEffect(() => {
    setNextButtonText('Siguiente');
    setVisualStepperIndex(currentStepIndex);
    setGoBack({
      action: onBackward
    });
    setDisabledNext(true);
  }, [
    setGoBack,
    onBackward,
    setVisualStepperIndex,
    currentStepIndex,
    setNextButtonText,
    setDisabledNext
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!validateCouponValue()) {
      setAlert({
        type: 'info',
        message: `Solo se permite realizar pagos mayores a ${COUPON_VALUES.MIN_VALUE} peso.`
      });
    }
  }, [validateCouponValue, setAlert]);

  useEffect(() => {
    window.scrollTo(0, document.body.scrollHeight);
  }, [paymentValueOption]);

  useEffect(() => {
    if (isFinancing) {
      return;
    }
    let ignoreRequest = false;

    const fetch = async () => {
      if (ignoreRequest) {
        return;
      }

      setLoading(true);

      const { contractId, couponId } = payment.detail;
      const token = currentUser ? currentUser.token : null;

      let recaptchaToken = await getValidateToken();

      const validateResponse = await ValidatePartialPaymentAPI(
        contractId,
        couponId,
        token,
        recaptchaToken
      );

      let partialPaymentValidation = {};
      if (validateResponse.success) {
        const {
          valid,
          canPayPartially: canPayPartial,
          isPartialCoupon
        } = validateResponse.data.data;

        partialPaymentValidation = {
          canBePaidPartially: canPayPartial && valid,
          isPartialCoupon
        };

        if (!valid) {
          setLoading(false);
          return;
        }
      } else {
        setLoading(false);
        return;
      }

      recaptchaToken = await getCheckToken();

      const checkResponse = await CheckPartialPaymentAPI(
        contractId,
        ValidPartialPaymentProductTypeIds,
        couponId,
        token,
        recaptchaToken
      );

      let paymentData;

      if (checkResponse.success) {
        const debtData = checkResponse.data.data;
        paymentData = [{ ...payment, debtData, ...partialPaymentValidation }];
      } else {
        paymentData = [{ ...payment, canBePaidPartially: false }];
      }

      setPaymentValueOption(
        paymentData[0].canBePaidPartially
          ? ''
          : PAYMENT_VALUE_OPTION.TotalCoupon
      );

      setPayment(paymentData);
      setLoading(false);
    };

    const { canBePaidPartially, debtData } = payment;

    if (
      !payment.hasOwnProperty('canBePaidPartially') ||
      (canBePaidPartially && !debtData)
    ) {
      fetch();
    }

    return () => {
      ignoreRequest = true;
    };
  }, [
    currentUser,
    payment,
    setPayment,
    setLoading,
    setPaymentValueOption,
    isFinancing,
    getValidateToken,
    getCheckToken
  ]);

  useEffect(() => {
    setDisabledNext(
      isDisabledPse ||
        isDisabledBreb ||
        isDisabledInsurance ||
        !validateCouponValue()
    );
  }, [
    isDisabledPse,
    isDisabledBreb,
    isDisabledInsurance,
    setDisabledNext,
    validateCouponValue
  ]);

  useEffect(() => {
    if (!Object.values(PAYMENT_METHOD).includes(methodSelected)) {
      setIsDisabledPse(true);
      setIsDisabledBreb(true);
      return;
    }

    // El formulario no seleccionado se desmonta, por lo que su estado
    // de "disabled" queda obsoleto y no debe seguir bloqueando el botón.
    if (methodSelected !== PAYMENT_METHOD.PSE) {
      setIsDisabledPse(false);
    }

    if (methodSelected !== PAYMENT_METHOD.BREB) {
      setIsDisabledBreb(false);
    }
  }, [methodSelected, setIsDisabledPse, setIsDisabledBreb]);

  useEffect(() => {
    const brebAmountValid = isBrebAmountValid();

    if (!brebAmountValid) {
      // Si BREB está seleccionado pero el monto no es válido, deshabilitarlo
      if (methodSelected === PAYMENT_METHOD.BREB) {
        setIsDisabledBreb(true);
      }
    }
  }, [isBrebAmountValid, methodSelected]);

  useEffect(() => {
    window.scrollTo(0, document.body.scrollHeight);
  }, []);

  const showInsuranceCard =
    isValidForInsurance &&
    silverPlanPrice &&
    INSURANCE_VALID_VALUE_OPTIONS.includes(paymentValueOption) &&
    !payment.isPartialCoupon &&
    Boolean(methodSelected) &&
    !isMeterRecharge;

  const renderStep = () => {
    if (isCardStep) {
      return <CardInfoForm />;
    }

    return (
      <>
        <FormProvider {...methods}>
          <form id={formId} onSubmit={handleSubmit(onForward)}>
            {methodSelected === PAYMENT_METHOD.CARD && (
              <ClientInfoForm
                setDisabledNext={setDisabledNext}
                setPayerInfo={setPayerInfo}
              />
            )}
            {methodSelected === PAYMENT_METHOD.PSE && (
              <PseInfoForm
                setDisabledNext={setIsDisabledPse}
                setPayerInfo={setPayerInfo}
                showTitle={false}
                extraPadding={!showInsuranceCard}
              />
            )}
            {methodSelected === PAYMENT_METHOD.BREB && (
              <BrebInfoForm
                setDisabledNext={setIsDisabledBreb}
                setPayerInfo={setPayerInfo}
                showTitle={false}
                extraPadding={!showInsuranceCard}
                disabled={isBrebPaymentMethodPending}
              />
            )}
          </form>
        </FormProvider>
      </>
    );
  };

  const renderPaymentCard = () => {
    const useOldCard =
      (isMultiplePayment && paymentsCount > 1) || payment.detail.couponId < 0;

    if (useOldCard) {
      return (
        <div>
          <NewPaymentCard details={payment.detail} type={payment.type} />
        </div>
      );
    }

    if (payment.type !== TYPE_OF_PAYMENTS.couponless) {
      return (
        <div>
          <NewCouponCard detail={payment.detail} type={payment.type} />
        </div>
      );
    }
  };

  const onClickPseMethodButton = useCallback(() => {
    if (methodSelected !== PAYMENT_METHOD.PSE) {
      setMethodSelected(PAYMENT_METHOD.PSE);
      setShowBrebAmountAlert(false);
    }
  }, [setMethodSelected, methodSelected]);

  const onClickBrebMethodButton = useCallback(() => {
    if (!isBrebAmountValid()) {
      setShowBrebAmountAlert(true);
      return;
    }
    if (methodSelected !== PAYMENT_METHOD.BREB) {
      setMethodSelected(PAYMENT_METHOD.BREB);
      setShowBrebAmountAlert(false);
    }
  }, [setMethodSelected, methodSelected, isBrebAmountValid]);

  const paymentMethods = useMemo(
    () => [
      {
        id: 'Payment_method_pse_image_button',
        label: getPaymentMethodLabel(PAYMENT_METHOD.PSE),
        image: PseImage,
        onClick: onClickPseMethodButton,
        selected: methodSelected === PAYMENT_METHOD.PSE
      },
      {
        id: 'Payment_method_breb_image_button',
        label: getPaymentMethodLabel(PAYMENT_METHOD.BREB),
        image: BrebImage,
        onClick: onClickBrebMethodButton,
        selected: methodSelected === PAYMENT_METHOD.BREB,
        disabled: !isBrebAmountValid(),
        hidden: !isBrebAvailable
      }
    ],
    [
      methodSelected,
      onClickPseMethodButton,
      onClickBrebMethodButton,
      isBrebAvailable,
      isBrebAmountValid
    ]
  );

  const showPaymentMethods = () => {
    if (paymentValueOption === PAYMENT_VALUE_OPTION.Partial) {
      if (
        payment.partialData &&
        payment.partialData.paymentValueOption === PAYMENT_VALUE_OPTION.Partial
      ) {
        return true;
      }
    } else if (
      Object.values(PAYMENT_VALUE_OPTION).includes(paymentValueOption)
    ) {
      return true;
    }

    return false;
  };

  const renderPaymentOptions = () => {
    return (
      <PaymentValueOptions
        payment={payment}
        setPayment={setPayment}
        currentUser={currentUser}
        setMethodSelected={setMethodSelected}
        paymentValueOption={paymentValueOption}
        setPaymentValueOption={setPaymentValueOption}
      />
    );
  };

  const renderPaymentMethods = () => {
    if (showPaymentMethods()) {
      return <PaymentMethodOptions methods={paymentMethods} />;
    }

    return <></>;
  };

  const renderInsuranceCard = () => {
    if (showInsuranceCard) {
      return (
        <div>
          <InsuranceAcquisitionCard
            setDisabledNext={setIsDisabledInsurance}
            setInsuranceInfo={setInsuranceInfo}
            price={silverPlanPrice}
            showPlanInfo={showPlanInfo}
            setShowPlanInfo={setShowPlanInfo}
          />
        </div>
      );
    }
  };

  return (
    <>
      <TurnstileInvisibleCaptcha
        captchaRef={validateCaptchaRef}
        onResolved={validateCaptchaHandlers.onResolved}
        onExpired={validateCaptchaHandlers.onExpired}
        onError={validateCaptchaHandlers.onError}
        onLoaded={validateCaptchaHandlers.onLoaded}
        action={RecaptchaAction.ValidatePartialPayments}
      />
      <TurnstileInvisibleCaptcha
        captchaRef={checkCaptchaRef}
        onResolved={checkCaptchaHandlers.onResolved}
        onExpired={checkCaptchaHandlers.onExpired}
        onError={checkCaptchaHandlers.onError}
        onLoaded={checkCaptchaHandlers.onLoaded}
        action={RecaptchaAction.CheckPartialPayments}
      />
      <Container maxWidth="md" className={classes.root}>
        {loading ? (
          <ModalProgress
            id="QueryCurrentDebtdata_progress_loading"
            message="Consultando tus deudas"
          />
        ) : (
          <>
            {showTimeoutBanner && (
              <SweetAlert
                id="payments_timeout_banner"
                type={ALERT_TYPE.WARNING}
                classes={{
                  root: classes.timeoutBanner,
                  message: classes.timeoutBannerText
                }}
                message={
                  <>
                    Tienes <strong>{formattedCountdown}</strong> para finalizar
                    tu pago o serás redireccionado al inicio.
                  </>
                }
              />
            )}
            {renderPaymentCard()}
            {isBrebPaymentMethodPending && (
              <SweetAlert
                id="AuthHome_div_isBrebPaymentMethodPending_info"
                classes={{ root: classes.brebSweetAlert }}
                type="warning"
                message={
                  <>
                    Actualmente tienes un proceso de pago activo con Bre-B.
                    Puedes pagar con el QR generado o utilizar otro medio de
                    pago.
                  </>
                }
              />
            )}
            {isBrebAvailable && !isBrebAmountValid() && (
              <SweetAlert
                id="payments_breb_invalid_amount_alert"
                classes={{ root: classes.brebSweetAlert }}
                type={ALERT_TYPE.WARNING}
                message={
                  <>
                    El pago con Bre-B solo está disponible para montos entre{' '}
                    <strong>{moneyWithDots(BREB_MIN_VALUE)}</strong> y{' '}
                    <strong>{moneyWithDots(BREB_MAX_VALUE)}</strong> de pesos.
                  </>
                }
              />
            )}
            <BrebAmountDialog
              open={showBrebAmountAlert}
              onClose={() => setShowBrebAmountAlert(false)}
              minimumAmount={moneyWithDots(BREB_MIN_VALUE)}
              maximumAmount={moneyWithDots(BREB_MAX_VALUE)}
            />
            {renderPaymentOptions()}
            {renderPaymentMethods()}
            {renderStep()}
            {renderInsuranceCard()}
          </>
        )}
      </Container>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    justifyContent: 'space-around',
    paddingLeft: theme.spacing(4),
    marginBottom: theme.spacing(12),
    paddingRight: theme.spacing(4),
    // * Responsive
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(),
      marginBottom: theme.spacing(12)
    }
  },
  inputContainer: {
    // * Mobile
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      marginTop: theme.spacing(),
      paddingTop: '0 !important',
      paddingBottom: '0 !important'
    }
  },
  selectableButtons: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-start'
    }
  },
  timeoutBanner: {
    marginBottom: theme.spacing(2),
    opacity: 1,
    width: '100%'
  },
  timeoutBannerText: {
    fontWeight: 600,
    fontSize: 14
  },
  brebSweetAlert: {
    margin: '8px 0'
  }
}));

export default PaymentsSecondStep;
